/**
 * The cross-link rails of a promotion page (web plan W0.9, master D-4): which
 * promotions "More from {Brand}" and "Similar {Category} deals" ask for, and
 * what "Where it's valid" lists when the promotion names no store of its own.
 *
 * Pure module (no `window`, no React), so the SEO server (plan W1) can ask the
 * backend exactly what the SPA asks for.
 *
 * The rails are fed by the public `publishedPromotions` feed, which already
 * applies the public-visibility rule and drops ended promotions (backend B0.4 /
 * B0.9): an ended page's rails show only offers that are still on.
 */
import { cityLabel, cleanName } from './text';

// The city naming rule lives in ./text (the store page's H1 uses it too).
export { cityLabel };

/** Cards per rail (master D-4: "up to 6"). */
export const RAIL_SIZE = 6;

/**
 * Rows asked for per rail: one more than shown, because the page's own
 * promotion can be among them and the public filter cannot exclude an id
 * (`_id` is not an allowed `where` field on the public mount).
 */
export const RAIL_FETCH_LIMIT = RAIL_SIZE + 1;

/** A category as the promotion query selects it (`path` is `all/<L1>/<L2>/<leaf>`). */
export interface RailCategorySource {
  id: string;
  title?: string | null;
  path?: string | null;
  parentCategory?: { id: string; title?: string | null; path?: string | null } | null;
}

/** The category a "Similar {Category} deals" rail is about. */
export interface RailCategory {
  /** Materialised path, e.g. `all/fashion-and-apparel/shoes-and-footwear`. */
  path: string;
  /** Cleaned title, e.g. "Shoes & Footwear". */
  title: string;
  /** 2 for the plan's L2 category; 1 when the promotion is only tagged at a top-level category. */
  level: 1 | 2;
}

/** Path segments below the synthetic root (`all/a/b` → `['all', 'a', 'b']`). */
function segments(path: string | null | undefined): string[] {
  return (path ?? '').split('/').filter(Boolean);
}

/** The ancestor of `path` at `level` (root = level 0), or null when `path` is shallower. */
function ancestorAt(path: string | null | undefined, level: number): string | null {
  const parts = segments(path);
  return parts.length > level ? parts.slice(0, level + 1).join('/') : null;
}

/**
 * The promotion's L2 category (the level under the top-level ones, e.g.
 * "Shoes & Footwear" under "Fashion & Apparel"): the L2 that most of its
 * categories sit in, the first one on a tie. A promotion tagged only at a
 * top-level category falls back to that one. The title comes from the
 * category itself or, for a deeper tag, its parent; a path whose title the
 * query didn't return is skipped rather than shown as a slug.
 */
export function promotionRailCategory(
  categories: readonly (RailCategorySource | null | undefined)[] | null | undefined,
): RailCategory | null {
  const titles = new Map<string, string>();
  for (const category of categories ?? []) {
    for (const node of [category, category?.parentCategory]) {
      const path = segments(node?.path).join('/');
      const title = cleanName(node?.title);
      if (path && title && !titles.has(path)) titles.set(path, title);
    }
  }

  for (const level of [2, 1] as const) {
    const counts = new Map<string, number>();
    for (const category of categories ?? []) {
      const path = ancestorAt(category?.path, level);
      if (path && titles.has(path)) counts.set(path, (counts.get(path) ?? 0) + 1);
    }
    let best: string | null = null;
    for (const [path, count] of counts) {
      if (best === null || count > counts.get(best)!) best = path;
    }
    if (best) return { path: best, title: titles.get(best)!, level };
  }
  return null;
}

const escapeRegex = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** `where` for "More from {Brand}": the brand's other listed promotions. */
export function brandRailWhere(brandId: string): string {
  return JSON.stringify({ brand: brandId });
}

/**
 * `where` for "Similar {Category} deals": promotions tagged at the category or
 * anywhere under it (never at a broader parent — those are not "similar"),
 * from other brands when the promotion has one, so the two rails never repeat
 * each other.
 */
export function similarRailWhere(category: Pick<RailCategory, 'path'>, excludeBrandId?: string | null): string {
  const inCategory = {
    $or: [
      { categoriesPaths: category.path },
      { categoriesPaths: { $regex: `^${escapeRegex(category.path)}/` } },
    ],
  };
  return JSON.stringify(excludeBrandId ? { $and: [inCategory, { brand: { $ne: excludeBrandId } }] } : inCategory);
}

/** At most `max` items, each id once, none of `exclude` (the page's own promotion, another rail's cards). */
export function railItems<T extends { id: string }>(
  items: readonly (T | null | undefined)[] | null | undefined,
  exclude: Iterable<string> = [],
  max = RAIL_SIZE,
): T[] {
  const seen = new Set(exclude);
  const out: T[] = [];
  for (const item of items ?? []) {
    if (!item || seen.has(item.id)) continue;
    seen.add(item.id);
    out.push(item);
    if (out.length >= max) break;
  }
  return out;
}

/** One city of a brand's stores. */
export interface CityCount {
  city: string;
  stores: number;
}

const HAS_UPPER = /\p{Lu}/u;

/**
 * The cities a brand's stores are in, most stores first, then A–Z: "Where
 * it's valid" for a brand-wide promotion that names no store of its own.
 * Names are cleaned and grouped case-insensitively, shown as the first
 * spelling with a capital letter (else `cityLabel`'s); stores without a city
 * are left out.
 */
export function brandCities(stores: readonly ({ city?: string | null } | null | undefined)[] | null | undefined): CityCount[] {
  const byKey = new Map<string, CityCount & { typed: boolean }>();
  for (const store of stores ?? []) {
    const city = cityLabel(store?.city);
    if (!city) continue;
    const typed = HAS_UPPER.test(cleanName(store?.city));
    const key = city.toLowerCase();
    const entry = byKey.get(key);
    if (!entry) byKey.set(key, { city, stores: 1, typed });
    else {
      entry.stores += 1;
      // A spelling typed with capitals ("DHA") wins over a generated one ("Dha").
      if (typed && !entry.typed) Object.assign(entry, { city, typed });
    }
  }
  return [...byKey.values()]
    .map(({ city, stores: count }) => ({ city, stores: count }))
    .sort((a, b) => b.stores - a.stores || a.city.localeCompare(b.city, 'en'));
}
